import type {NewsBlockType} from 'types/payload-types';

/**
 * Custom hook that determines which news articles to display based on the autoFill configuration.
 * This hook provides a unified interface for accessing either manually selected news articles or
 * automatically populated news articles from the NewsBlock component. It simplifies the logic
 * for components that need to conditionally display different sets of news data.
 *
 * @param newsArticles     Manually selected news articles from the NewsBlock configuration.
 * @param newsArticlesAuto Automatically populated news articles from the NewsBlock configuration.
 * @param autoFill         Boolean flag that determines whether to use auto-populated or manual news articles.
 * @returns Object containing the selected news articles based on the autoFill parameter.
 *
 * @example
 * ```tsx
 * const { news } = useNewsBlock(
 *     true,
 *     manuallySelectedArticles,
 *     autoPopulatedArticles
 * );
 * ```
 */
export const useNewsBlock = (
    newsArticles: NewsBlockType['newsArticles'],
    newsArticlesAuto: NewsBlockType['newsArticlesAuto'],
    autoFill?: boolean | null
) => {
    const news = autoFill ? newsArticlesAuto : newsArticles;

    return {news};
};